import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import DateFormatter from "../../../components/DateFormatter";
import { useFeedBack } from "../../../context/FeedBackContext";
import { useAuth } from "../../../context/AuthContext";
import SystemInfo from "../../../util/SystemInfo";
import useAxios from "../../../hooks/useAxios";
import { dateFine } from "../../../util/Utilities";
import { FaDownload, FaFileAlt, FaEdit } from "react-icons/fa";

const ProductObservationsDetails = () => {

    const navigate = useNavigate();
    const { id } = useParams();
    const { setLoading, setCustomAlert } = useFeedBack();
    const { user, permissions, isSuperAdmin } = useAuth();

    const systemCode = SystemInfo?.systemCode || 'sys-002';

    const [currentDetails, setCurrentDetails] = useState(null);
    const [downloadingFileId, setDownloadingFileId] = useState(null);

    const isOwner =
        (currentDetails?.user_id && Number(currentDetails?.user_id) === Number(user?.id)) ||
        (currentDetails?.created_by && Number(currentDetails?.created_by) === Number(user?.id)) ||
        (currentDetails?.user?.id && Number(currentDetails?.user?.id) === Number(user?.id)) ||
        (currentDetails?.createdBy?.id && Number(currentDetails?.createdBy?.id) === Number(user?.id));

    const canEdit = isSuperAdmin || permissions?.includes(`${systemCode}-update-observations`) || isOwner;

    const [{ data: observationDetails, loading: loadingDetails }] = useAxios({ url: `/product-observations/${id}` }, { useCache: false });
    const [{ loading: downloadingFile }, execDownload] = useAxios({ responseType: 'blob' }, { manual: true, useCache: false });

    const handleDownloadEvidence = (file) => {
        setDownloadingFileId(file.id);
        execDownload({
            url: `/product-observations/${id}/files/${file.id}/download`
        })
            .then((res) => {
                const blob = new Blob([res.data]);
                const blobUrl = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = file.original_name || `archivo_${file.id}`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(blobUrl);
            })
            .catch((err) => {
                console.error(err);
                setCustomAlert?.({
                    title: 'Error',
                    severity: 'error',
                    message: 'No se pudo descargar el archivo de prueba.',
                    show: true
                });
            })
            .finally(() => {
                setDownloadingFileId(null);
            });
    };

    useEffect(() => {
        if (observationDetails) {
            setCurrentDetails(observationDetails?.data);
        }
    }, [observationDetails]);

    useEffect(() => {
        setLoading?.({
            show: loadingDetails,
            message: 'Obteniendo Información de la Observación'
        });
    }, [loadingDetails]);

    return (
        <div>
            <div className="text-end my-4">
                <button className="btn btn-outline-secondary" onClick={() => navigate?.(-1)}>
                    Volver Al Listado
                </button>
                {canEdit && (
                    <Link to={`/observaciones/editar/${id}`} className="ms-2 btn btn-warning text-dark">
                        <FaEdit className="me-1" /> Editar Observación
                    </Link>
                )}
                <Link to="/observaciones/crear" className="ms-2 btn btn-primary">
                    Crear Nueva Observación
                </Link>
            </div>

            <div className="row">
                <div className="col-md-12">
                    <div className="card">
                        <div className="card-header">
                            <h5 className="card-title">
                                Detalle de Observación #{currentDetails?.id}
                            </h5>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-4 mb-3">
                                    <h5>N° Guía / Factura</h5>
                                    <input type="text" readOnly value={currentDetails?.guide_number || '--'} className="form-control" />
                                </div>
                                <div className="col-md-4 mb-3">
                                    <h5>N° Orden de Compra</h5>
                                    <input type="text" readOnly value={currentDetails?.purchase_order_number || '--'} className="form-control" />
                                </div>
                                <div className="col-md-4 mb-3">
                                    <h5>Proveedor</h5>
                                    <input type="text" readOnly value={currentDetails?.provider?.name || '--'} className="form-control" />
                                </div>
                                <div className="col-md-4 mb-3">
                                    <h5>Usuario Solicitante</h5>
                                    <input type="text" readOnly value={currentDetails?.user ? `${currentDetails.user.name} (${currentDetails.user.documentNumber || '--'})` : '--'} className="form-control" />
                                </div>
                                <div className="col-md-4 mb-3">
                                    <h5>Elaborado por</h5>
                                    <input type="text" readOnly value={currentDetails?.createdBy?.name || '--'} className="form-control" />
                                </div>
                                <div className="col-md-4 mb-3">
                                    <h5>Fecha de Creación</h5>
                                    <input
                                        type="text"
                                        readOnly
                                        value={currentDetails?.createdAt ? dateFine(currentDetails.createdAt) : '--'}
                                        className="form-control"
                                    />
                                </div>
                                <div className="col-md-12 mt-3">
                                    <h5 className="text-primary fw-bold">Observación Global</h5>
                                    <textarea readOnly value={currentDetails?.observation || '--'} className="form-control mb-3" rows={4} />
                                </div>

                                <div className="col-md-12 mt-4">
                                    <h5 className="text-primary fw-bold mb-3">
                                        Productos Observados ({currentDetails?.products?.length || 0})
                                    </h5>
                                    <div className="table-responsive">
                                        <table className="table table-striped table-bordered align-middle">
                                            <thead className="table-light">
                                                <tr>
                                                    <th style={{ width: 50 }}>#</th>
                                                    <th>Código</th>
                                                    <th>Nombre del Producto</th>
                                                    <th>Referencia</th>
                                                    <th>Categoría</th>
                                                    <th style={{ width: 100 }} className="text-center">Cantidad</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentDetails?.products?.length > 0 ? (
                                                    currentDetails.products.map((prod, i) => (
                                                        <tr key={prod.id || i}>
                                                            <td><b>{i + 1}</b></td>
                                                            <td><code>{prod.code || '--'}</code></td>
                                                            <td>{prod.name}</td>
                                                            <td>{prod.reference || '--'}</td>
                                                            <td>{prod.category?.name || '--'}</td>
                                                            <td className="text-center fw-bold">{prod.quantity ?? prod.pivot?.quantity ?? 1}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={6} className="text-center">
                                                            No hay productos asociados a esta observación.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div className="col-md-12 mt-4">
                                    <h5 className="text-primary fw-bold mb-3">
                                        Archivos de Prueba / Evidencia ({currentDetails?.files?.length || 0})
                                    </h5>
                                    {currentDetails?.files?.length > 0 ? (
                                        <div className="table-responsive">
                                            <table className="table table-striped table-bordered align-middle">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th style={{ width: 50 }}>#</th>
                                                        <th>Nombre del Archivo</th>
                                                        <th>Tamaño</th>
                                                        <th>Fecha de Carga</th>
                                                        <th style={{ width: 140 }} className="text-center">Acción</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {currentDetails.files.map((file, idx) => (
                                                        <tr key={file.id || idx}>
                                                            <td><b>{idx + 1}</b></td>
                                                            <td>
                                                                <FaFileAlt className="text-primary me-2" />
                                                                {file.original_name}
                                                            </td>
                                                            <td>
                                                                {file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : '--'}
                                                            </td>
                                                            <td>
                                                                {file.createdAt ? dateFine(file.createdAt) : '--'}
                                                            </td>
                                                            <td className="text-center">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-primary d-inline-flex align-items-center"
                                                                    onClick={() => handleDownloadEvidence(file)}
                                                                    disabled={downloadingFileId === file.id}
                                                                >
                                                                    {downloadingFileId === file.id ? (
                                                                        <span className="spinner-border spinner-border-sm me-1" role="status" />
                                                                    ) : (
                                                                        <FaDownload className="me-1" />
                                                                    )}
                                                                    Descargar
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="alert alert-secondary mb-0">
                                            No se adjuntaron archivos de prueba para esta observación.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductObservationsDetails;
